// @flow

import {bindAll, extend} from '../../util/util';
import {ContentVisibilityEvent, ContentRemoveEvent} from '../../../src/ui/events';
import {Common} from '../../util/enums';
import type Map from '../map';
import type {ContentInterface} from './content';

export type Options = {
    layerId: string,
    isVisible: boolean
};

const defaultOptions: Options = {
    layerId: '',
    isVisible: true
};

/**
 * Content Layer Base Class
 */
export class ContentBase implements ContentInterface {
    _map: Map;
    _options: Options;
    _layerNames: Array<string>;
    _isVisible: boolean;

    constructor(options: Options) {
        this._options = extend({}, defaultOptions, options);
        this._layerNames = [];
        this._isVisible = this._options.isVisible === true;

        bindAll([
            '_onStyleChange'
        ], this);
    }

    _getContent() {}

    /**
     * Add the layer to a map.
     * @param {*} map The map
     * @returns this
     */
    addTo(map: Map) {
        if (map.getLayer(this._layerNames[0])) {
            throw new Error(`The layer (${this._layerNames[0]}) already exists on this map.`);
        }
        this._map = map;
        this._map.on('style.load', this._onStyleChange);
        this._getContent();
        return this;
    }

    /**
     * Remove the layer from the map.
     * @returns this
     */
    remove() {
        if (this._map) {
            this._layerNames.forEach((l) => {
                if (this._map.getLayer(l)) {
                    this._map.removeLayer(l);
                }
            });
            this._map.off('style.load', this._onStyleChange);
            /**
             * Fired when layer is removed.
             *
             * @event content.remove
             * @memberof Map
             * @instance
             * @type {Object}
             * @property {ContentRemoveEvent} object with layer id has been returned.
             */
            this._map.fire(new ContentRemoveEvent(
                'content.remove',
                {id: this._options.layerId}
            ));
            delete this._map;
        }
        return this;
    }

    /**
     * Returns true if the layer is enabled. Visibility of layer content may be affected by other factors, such as zoom level.
     * @returns {boolean} A boolean indicating the visibility of the layer.
     */
    isVisible() {
        // When style.load happens, the actual layer is removed. The this._isVisible value could be different from the reality briefly while adding the layer.
        // This could be a minor risk.
        return this._isVisible;
    }

    /**
     * Set the visibility of the layer. Visibility of layer content may be affected by other factors, such as zoom level.
     * @param {boolean} isVisible A boolean indicating the visibility of the layer.
     * @returns {ContentBase} `this`
     */
    setVisibility(isVisible: boolean) {
        this._isVisible = isVisible;
        if (this._map) {
            const layer = this._map.getLayer(this._layerNames[0]);
            if (layer) {
                const layerVisibility = this._map.getLayoutProperty(this._layerNames[0], 'visibility');
                const visibility = (isVisible) ? 'visible' : 'none';
                if (visibility !== layerVisibility) {
                    this._layerNames.forEach((l) => {
                        if (this._map.getLayer(l)) {
                            this._map.setLayoutProperty(l, 'visibility', visibility);
                        }
                    });
                    this._map._update(true);
                    /**
                     * Fired when layer visibility changes.
                     *
                     * @event content.visibility
                     * @memberof Map
                     * @instance
                     * @type {Object}
                     * @property {ContentVisibilityEvent} object with layer id has been returned.
                     */
                    this._map.fire(new ContentVisibilityEvent(
                        'content.visibility',
                        {
                            id: this._options.layerId,
                            isVisible,
                        }
                    ));
                }
            }
        }
        return this;
    }

    /**
     * Toggle the visibility of the layer. Visibility of layer content may be affected by other factors, such as zoom level.
     * @returns {ContentBase} `this`
     */
    toggleVisibility() {
        return this.setVisibility(!this.isVisible());
    }

    _getStyleName() {
        // custom styles should have a template property in metadata. This should be
        // one of our standard style names to use as a base
        const styleObj = this._map.getStyle();
        const styleName = (styleObj && styleObj.metadata && styleObj.metadata.template) ? styleObj.metadata.template : this._map._styleName;

        // if the style name is in the enum, return the name, otherwise default to transportation
        return (Object.values(Common.Style).includes(styleName)) ? styleName : Common.Style.TRANSPORTATION;
    }

    _onStyleChange() {
        this._getContent();
    }
}
